import LayoutHR from "layouts/layout-hr";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import { DeductionProvider, useDeduction } from "./context/DeductionContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { deleteRequest } from "services/api";

function DeductionContent() {
  const { deductions, filterOptions, handleSearch, refetch, Pagination } =
    useDeduction();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const {
    // handleSuccess,
    handleError,
    // showCanvasHandler,
    handleSuccessToast,
    // handleLoadingThenSuccessToast,
  } = useCanvasHandler();

  // Handle create new deduction
  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  // Handle edit deduction
  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  // Handle delete deduction
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/deductions/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Deduction deleted successfully");
          refetch(); // Refresh the data
        } else {
          handleError("Failed to delete deduction");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Deduction saved successfully (Demo Mode)");
  };

  // Handle form error
  const handleFormError = (error) => {
    handleError(error);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Form fields configuration
  const formFields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      placeholder: "Enter deduction name",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Enter deduction description",
    },
    {
      name: "price",
      label: "Price",
      type: "number",
      required: true,
      placeholder: "Enter deduction price",
    },
    {
      name: "type",
      label: "Type",
      type: "select",
      required: true,
      options: [
        { value: "once", label: "Once" },
        { value: "multiple", label: "Multiple" },
      ],
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Deduction Management</h2>
              <div className="text-muted mt-1">
                Manage salary deductions and their pricing
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Deduction
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search deductions..."
                    value={filterOptions.search}
                    onChange={(e) => {
                      // Update search value in filterOptions
                      // const newSearch = e.target.value;
                      // Note: Actual search is handled on Enter key press
                    }}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Description</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.loading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading deductions...
                      </td>
                    </tr>
                  ) : deductions.data?.length > 0 ? (
                    deductions.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.name}</td>
                        <td>Rp {item.price?.toLocaleString()}</td>
                        <td>{item.description}</td>
                        <td>{item.type}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No deductions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {deductions.pagination && <Pagination />}
          </div>
        </div>

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem ? "Edit Deduction" : "Create Deduction"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/deductions"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function DeductionPage() {
  return (
    <DeductionProvider>
      <DeductionContent />
    </DeductionProvider>
  );
}
